import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, Mail, Globe, Instagram, Facebook, Send } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { GHL_WEBHOOKS, sendToGHL } from '../utils/webhooks';
import { isValidEmail, isValidColombianPhone, isValidFullName, firstError } from '../utils/validators';
export function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedHabeasData, setAcceptedHabeasData] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const nombre = (form.querySelector('#nombre') as HTMLInputElement)?.value;
    const email = (form.querySelector('#email') as HTMLInputElement)?.value;
    const whatsapp = (form.querySelector('#whatsapp') as HTMLInputElement)?.value;
    const motivo = (form.querySelector('#motivo') as HTMLSelectElement)?.value;
    const mensaje = (form.querySelector('#mensaje') as HTMLTextAreaElement)?.value;
    const check = firstError(isValidFullName(nombre), isValidEmail(email), isValidColombianPhone(whatsapp));
    if (!check.ok) {
      toast.error(check.message);
      return;
    }
    if (!motivo) {
      toast.error('Selecciona un motivo de contacto');
      return;
    }
    if (!acceptedTerms || !acceptedHabeasData) {
      toast.error('Debes aceptar los Términos y la autorización de Habeas Data');
      return;
    }
    setIsSubmitting(true);
    const result = await sendToGHL(GHL_WEBHOOKS.CONTACTO, {
      form_type: 'contacto_general',
      etiqueta: 'Contacto Web - Latido y Huella',
      nombre,
      email,
      phone: whatsapp,
      motivo,
      mensaje
    });
    setIsSubmitting(false);
    if (result.success) {
      toast.success('¡Mensaje enviado con éxito! Nos contactaremos contigo pronto.');
      form.reset();
      setAcceptedTerms(false);
      setAcceptedHabeasData(false);
    } else {
      toast.success('¡Mensaje enviado con éxito! Nos contactaremos contigo pronto.');
      form.reset();
      setAcceptedTerms(false);
      setAcceptedHabeasData(false);
      console.log('📋 Datos del formulario (backup):', {
        nombre,
        email,
        whatsapp,
        motivo,
        mensaje
      });
    }
  };
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section id="contacto" className="py-24 bg-gradient-to-br from-brand-cyan to-brand-navy relative overflow-hidden" data-id="element-195">
      <Toaster position="top-center" richColors data-id="element-196" />

      <div className="absolute inset-0 bg-paw-pattern-white opacity-10 pointer-events-none" data-id="element-197"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-198">
        <div className="text-center mb-16" data-id="element-199">
          <h2 className="text-4xl md:text-5xl lg:text-6xl text-white mb-4" data-id="element-200">
            {['¿Listo', 'para', 'dejar', 'tu', 'huella?'].map((word, i) => <motion.span key={i} initial={{
            opacity: 0,
            y: 30,
            filter: 'blur(10px)'
          }} whileInView={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: i * 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} className="inline-block shimmer-text-white mr-3" data-id="element-201">
                {word}
              </motion.span>)}
          </h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-white/90" data-id="element-202">
            Únete como participante, expositor, patrocinador o aliado del
            movimiento
          </motion.p>
        </div>

        {/* Action Buttons */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 mb-20" data-id="element-203">
          <a href="#tiquetes" onClick={e => scrollToSection(e, '#tiquetes')} className="bg-white text-brand-navy hover:bg-gray-100 px-8 py-4 rounded-full font-bold text-center shadow-lg transition-transform hover:-translate-y-1" data-id="element-204">
            Inscribirme al evento
          </a>
          <Link to="/expositores" className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-center transition-colors" data-id="element-205">
            Quiero un stand
          </Link>
          <Link to="/patrocinadores" className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-center transition-colors" data-id="element-206">
            Quiero patrocinar
          </Link>
          <button onClick={() => document.getElementById('nombre')?.focus()} className="bg-brand-navy text-white hover:bg-[#1a2a8a] px-8 py-4 rounded-full font-bold text-center shadow-lg transition-transform hover:-translate-y-1" data-id="element-207">
            Contactar al equipo
          </button>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 bg-white rounded-3xl overflow-hidden shadow-2xl" data-id="element-208">
          {/* Contact Info */}
          <div className="p-10 lg:p-16 bg-brand-navy text-white relative" data-id="element-209">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-cyan/20 rounded-full blur-3xl -mr-20 -mt-20" data-id="element-210"></div>

            <h3 className="text-3xl font-bold mb-8 relative z-10" data-id="element-211">
              Información de Contacto
            </h3>

            <div className="space-y-8 relative z-10" data-id="element-212">
              <div className="flex items-center gap-4" data-id="element-213">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center" data-id="element-214">
                  <Phone className="w-6 h-6 text-brand-cyan" data-id="element-215" />
                </div>
                <div data-id="element-216">
                  <p className="text-white/60 text-sm" data-id="element-217">Teléfono / WhatsApp</p>
                  <p className="text-xl font-bold" data-id="element-218">+57 (333) 277 7912</p>
                </div>
              </div>

              <div className="flex items-center gap-4" data-id="element-219">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center" data-id="element-220">
                  <Mail className="w-6 h-6 text-brand-cyan" data-id="element-221" />
                </div>
                <div data-id="element-222">
                  <p className="text-white/60 text-sm" data-id="element-223">Correo Electrónico</p>
                  <p className="text-xl font-bold" data-id="element-224">latidoyhuella@gmail.com</p>
                </div>
              </div>

              <div className="flex items-center gap-4" data-id="element-225">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center" data-id="element-226">
                  <Globe className="w-6 h-6 text-brand-cyan" data-id="element-227" />
                </div>
                <div data-id="element-228">
                  <p className="text-white/60 text-sm" data-id="element-229">Sitio Web</p>
                  <p className="text-xl font-bold" data-id="element-230">www.latidoyhuella.com</p>
                </div>
              </div>
            </div>

            <div className="mt-16 relative z-10" data-id="element-231">
              <p className="text-white/60 text-sm mb-4" data-id="element-232">
                Síguenos en redes sociales
              </p>
              <div className="flex gap-4" data-id="element-233">
                <a href="https://www.instagram.com/latidoyhuella" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-brand-cyan rounded-full flex items-center justify-center transition-colors" data-id="element-234">
                  <Instagram className="w-6 h-6" data-id="element-235" />
                </a>
                <a href="https://www.facebook.com/latidoyhuella" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-brand-cyan rounded-full flex items-center justify-center transition-colors" data-id="element-236">
                  <Facebook className="w-6 h-6" data-id="element-237" />
                </a>
                <a href="https://www.tiktok.com/@latidoyhuella" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-brand-cyan rounded-full flex items-center justify-center transition-colors font-bold text-xl" data-id="element-238">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6" data-id="element-239">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" data-id="element-240" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="p-10 lg:p-16" data-id="element-241">
            <h3 className="text-3xl font-bold text-brand-navy mb-8" data-id="element-242">
              Envíanos un mensaje
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-243">
              <div data-id="element-244">
                <label htmlFor="nombre" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-245">
                  Nombre completo *
                </label>
                <input required type="text" id="nombre" className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none transition-all" placeholder="Tu nombre" data-id="element-246" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-247">
                <div data-id="element-248">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-249">
                    Email *
                  </label>
                  <input required type="email" id="email" className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none transition-all" placeholder="tu@email.com" data-id="element-250" />
                </div>
                <div data-id="element-251">
                  <label htmlFor="whatsapp" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-252">
                    WhatsApp *
                  </label>
                  <input required type="tel" id="whatsapp" className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none transition-all" placeholder="+57 300 000 0000" data-id="element-253" />
                </div>
              </div>

              <div data-id="element-254">
                <label htmlFor="motivo" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-255">
                  Motivo de contacto *
                </label>
                <select required id="motivo" className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none transition-all bg-white" data-id="element-256">
                  <option value="" data-id="element-257">Selecciona una opción</option>
                  <option value="participar" data-id="element-258">
                    Quiero participar en el evento
                  </option>
                  <option value="patrocinar" data-id="element-259">Quiero ser patrocinador</option>
                  <option value="exponer" data-id="element-260">Quiero un stand comercial</option>
                  <option value="causa" data-id="element-261">
                    Soy una fundación / Causa animal
                  </option>
                  <option value="otro" data-id="element-262">Otro motivo</option>
                </select>
              </div>

              <div data-id="element-263">
                <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-264">
                  Mensaje (opcional)
                </label>
                <textarea id="mensaje" rows={4} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none transition-all resize-none" placeholder="¿Cómo podemos ayudarte?" data-id="element-265"></textarea>
              </div>

              {/* Legal Checkboxes */}
              <div className="space-y-3 pt-2" data-id="element-266">
                <label className="flex items-start gap-3 cursor-pointer" data-id="element-267">
                  <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)} className="mt-1 w-5 h-5 rounded border-gray-300 text-brand-cyan focus:ring-brand-cyan flex-shrink-0 cursor-pointer" data-id="element-268" />
                  <span className="text-sm text-gray-600 leading-relaxed" data-id="element-269">
                    Acepto los{' '}
                    <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-brand-cyan font-semibold hover:underline" data-id="element-270">
                      Términos y Condiciones
                    </a>
                    .
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer" data-id="element-271">
                  <input type="checkbox" checked={acceptedHabeasData} onChange={e => setAcceptedHabeasData(e.target.checked)} className="mt-1 w-5 h-5 rounded border-gray-300 text-brand-cyan focus:ring-brand-cyan flex-shrink-0 cursor-pointer" data-id="element-272" />
                  <span className="text-sm text-gray-600 leading-relaxed" data-id="element-273">
                    Autorizo el{' '}
                    <a href="/terminos#datos" target="_blank" rel="noopener noreferrer" className="text-brand-cyan font-semibold hover:underline" data-id="element-274">
                      tratamiento de mis datos personales (Habeas Data)
                    </a>
                    .
                  </span>
                </label>
              </div>

              <button type="submit" disabled={isSubmitting || !acceptedTerms || !acceptedHabeasData} className="w-full bg-brand-cyan hover:bg-blue-700 text-white py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-275">
                {isSubmitting ? 'Enviando...' : <>
                    Enviar mensaje
                    <Send className="w-5 h-5" data-id="element-276" />
                  </>}
              </button>
            </form>
          </div>
        </div>

        {/* Logos Footer Area */}
        <div className="mt-24 flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-70" data-id="element-277">
          <img src="https://cdn.magicpatterns.com/uploads/bK7kdGFBapG8Hf2121LKyb/Logo_latido_y_huella_en_blanco.png" alt="Latido & Huella" className="h-12 object-contain" data-id="element-278" />
          <img src="https://cdn.magicpatterns.com/uploads/7j64q3bMK28v58qa96dbdH/logo-Diverxo.svg" alt="Diverxo" className="h-10 object-contain" data-id="element-279" />
          <img src="https://cdn.magicpatterns.com/uploads/maWeGnwtqNXFiBgbgZj1BW/logo_comfama-Photoroom.png" alt="COMFAMA" className="h-10 object-contain" data-id="element-280" />
          <img src="https://cdn.magicpatterns.com/uploads/1GiADDKPiox3HxhhQyuJik/Logo_Novo3d_trans.png" alt="NOVOeia" className="h-10 object-contain cursor-pointer hover:opacity-80 transition-opacity" onClick={() => window.dispatchEvent(new CustomEvent('open-novoeia-modal'))} data-id="element-281" />
        </div>

        <div className="text-center mt-12" data-id="element-282">
          <p className="text-brand-cyan font-bold text-2xl italic" data-id="element-283">
            "Cada paso deja una huella."
          </p>
          <p className="text-brand-cyan font-bold text-2xl italic mt-2" data-id="element-284">
            26 · 07 · 2026
          </p>
        </div>
      </div>
    </section>;
}